/**
 * Application Color Palette
 *
 * Centralized color definitions for consistent theming across the application.
 * These colors should be used for charts, visualizations, and UI elements.
 */

export const colorPalette = {
  // Primary palette - ordered from dark to light within each color family
  darkBlue: '#245482',
  mediumBlue: '#7fbad4',
  lightBlue: '#bee6e8',

  lightGreen: '#bee5ac',
  mediumGreen: '#7fb459',
  darkGreen: '#24822a',

  mediumGray: '#7b8b96',
  darkGray: '#545f66',
  lightGray: '#ebecec',
};

/**
 * Chart color schemes for different visualization types.
 * Colors are selected to have sufficient contrast and avoid confusion.
 */
export const chartColors = {
  /**
   * Primary chart palette - use for most multi-series charts
   * Colors are well-differentiated to avoid confusion
   */
  primary: [
    colorPalette.darkBlue, // #245482
    colorPalette.mediumBlue, // #7fbad4
    colorPalette.darkGreen, // #24822a
    colorPalette.mediumGray, // #7b8b96
  ],

  /**
   * Extended chart palette - use when more than 4 colors are needed
   * Includes all distinct colors from the palette
   */
  extended: [
    colorPalette.darkBlue, // #245482
    colorPalette.mediumBlue, // #7fbad4
    colorPalette.darkGreen, // #24822a
    colorPalette.mediumGray, // #7b8b96
    colorPalette.mediumGreen, // #7fb459
    colorPalette.lightBlue, // #bee6e8
    colorPalette.lightGreen, // #bee5ac
    colorPalette.darkGray, // #545f66
  ],

  /**
   * Sequential palette - use for heatmaps or sequential data
   * Progresses from light to dark within a color family
   */
  sequential: {
    blue: [
      colorPalette.lightBlue, // #bee6e8
      colorPalette.mediumBlue, // #7fbad4
      colorPalette.darkBlue, // #245482
    ],
    green: [
      colorPalette.lightGreen, // #bee5ac
      colorPalette.mediumGreen, // #7fb459
      colorPalette.darkGreen, // #24822a
    ],
    gray: [
      colorPalette.lightGray, // #ebecec
      colorPalette.mediumGray, // #7b8b96
      colorPalette.darkGray, // #545f66
    ],
  },
};

/**
 * Semantic colors for specific use cases
 */
export const semanticColors = {
  // Waste level indicators
  waste: {
    high: '#f44336', // Red (keep existing for high waste)
    medium: '#ff9800', // Orange (keep existing for medium waste)
    low: colorPalette.mediumGreen, // #7fb459 - use palette green
  },

  // Status indicators
  success: colorPalette.darkGreen, // #24822a
  warning: '#ff9800', // Orange
  error: '#f44336', // Red
  info: colorPalette.darkBlue, // #245482

  // Alignment indicators
  alignment: {
    ashp: colorPalette.darkBlue, // #245482
    pediatric: colorPalette.mediumGreen, // #7fb459
  },
};

export default {
  colorPalette,
  chartColors,
  semanticColors,
};
